import { CACHE_REFETCH_INTERVAL, CALENDAR_SLOTS_CACHE_STALE_TIME } from 'app.constants'
import { CalendarSlot, CalendarSlotApiResponse } from 'types'
import { getIsoStringYearMonthDay, useMutationHandler, useQueryHandler, getShopId, getLocationId } from 'utils'
import { customerQuery } from './customer.api'

//slots:
export const useCalendarSlots = (
    resourceIds: string[],
    date: Date,
    timezone: string | null,
    enabled: boolean
) => {

    const shopId = getShopId()
    const locationId = getLocationId()
    const isoStringYearMonthDay = getIsoStringYearMonthDay(date)

    return useQueryHandler<CalendarSlotApiResponse>({
        query: `
            query getCalendarBlockSlots(
                $request: GetCalendarBlockSlotsRequest!
            ){
                getCalendarBlockSlots(
                    request: $request
                ){

                    date
                    now

                    resources{

                        id
                        
                        slots{

                            dateTime
                            duration
                            variantTitle

                            service{
                                id
                                title
                                groupAppointments
                                groupAppointmentSlots
                            }

                            items{

                                id
                                type
                                quantity

                                customer{
                                    ${customerQuery}
                                }

                            }

                        }

                    }

                }
            }
        `,
        variables: {
            request: {
                shop: shopId,
                locationId,
                date: isoStringYearMonthDay,
                timezone,
                resourceIds
            }
        },
        cacheKey: ['calendarSlots', isoStringYearMonthDay, timezone, resourceIds, shopId, locationId],
        onSuccess: json => {
            const response: CalendarSlotApiResponse = json.data.getCalendarBlockSlots
            return {
                ...response,
                resources: response.resources.map(resource => {
                    return {
                        ...resource,
                        slots: normalizeSlots(resource.slots)
                    }
                })
            }
        },
        enabled,
        staleTime: CALENDAR_SLOTS_CACHE_STALE_TIME,
        refetchInterval: CACHE_REFETCH_INTERVAL,
        keepPreviousData: true
    })

}

//normalizeSlots:
//the API can return more than one slot object for the same time block — for
//example one slot carrying its appointments and another carrying a reservation.
//Every slot is positioned absolutely by its dateTime, so when left unmerged these
//slots overlap perfectly and one covers the others, hiding the covered bookings.
//Slots that share a block identity (dateTime + duration + service + variant) are
//merged into a single slot whose items array holds all of their bookings. This
//matches the intended shape (a single slot carries mixed item types together, see
//src/data/mock/calendar.mock.ts) and is what the renderers already expect
//(findSlotType / findCustomerName / SlotDetails / buildPrintRows all operate on a
//slot's items array). The result is then sorted by time:
const normalizeSlots = (slots: CalendarSlot[]) => {

    const mergedSlots = new Map<string, CalendarSlot>()
    slots.forEach(slot => {
        const identity = `${slot.dateTime} ${slot.duration} ${slot.service?.id ?? ''} ${slot.variantTitle ?? ''}`
        const existing = mergedSlots.get(identity)
        if(existing){
            mergedSlots.set(identity, { ...existing, items: [...existing.items, ...slot.items] })
        }else{
            mergedSlots.set(identity, slot)
        }
    })

    return sortSlots(Array.from(mergedSlots.values()))

}

//sortSlots:
const sortSlots = (slots: CalendarSlot[]) => {

    const compare = (a: CalendarSlot, b: CalendarSlot) => {
        if(new Date(a.dateTime).getTime() < new Date(b.dateTime).getTime()){
          return -1
        }
        if(new Date(a.dateTime).getTime() > new Date(b.dateTime).getTime()){
          return 1
        }
        return 0
    }

    return slots.sort(compare)

}

//check in:
export const useAppointmentCheckIn = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation checkedInAppointment(
            $shop: String,
            $id: String!,
            $note: String!
        ){
            checkedInAppointment(
                shop: $shop,
                id: $id,
                note: $note
            ){
                id
            }
        }
    `,
    variables: {
        shop: getShopId(),
        id: appointmentId,
        note: ''
    },
    onSuccess: json => json.data.checkedInAppointment.id === appointmentId
})

//resend email:
export const useAppointmentResendConfirmEmail = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation resendAppointmentConfirmation(
            $shop: String!,
            $id: String!
        ){
            resendAppointmentConfirmation(
                shop: $shop,
                id: $id
            ){
                message
            }
        }
    `,
    variables: {
        shop: getShopId(),
        id: appointmentId
    },
    onSuccess: json => json.data.resendAppointmentConfirmation.message === 'Done'
})

//no show:
export const useAppointmentNoShow = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation noShowAppointment (
            $shop: String!,
            $id: String!,
            $note: String!
        ){
            noShowAppointment (
                shop: $shop,
                id: $id,
                note: $note
            ){
                id
            }
        }
    `,
    variables: {
        shop: getShopId(),
        id: appointmentId,
        note: ''
    },
    onSuccess: json => json.data.noShowAppointment.id === appointmentId
})

export const useAppointmentCancel = (appointmentId: string, refund: boolean) => useMutationHandler<boolean>({
    query: `
        mutation cancelAppointment (
            $shop: String!,
            $id: String!,
            $request: CancelAppointmentRequest!
        ) {
            cancelAppointment (
                shop: $shop,
                id: $id,
                request: $request
            ){
                message
            }
        }
    `,
    variables: {
        shop: getShopId(),
        id: appointmentId,
        request: {
            refundMoney: refund,
            content: null
        }
    },
    onSuccess: json => json.data.cancelAppointment.message === 'Done'
})
